import { RefData } from '@rialtic/utils'
import type { Indication } from '../types'
import {
  parseAgeValue,
  toPositiveNumber,
  type DdeIndicationInput,
} from './ddeRows'
import { isIcd10InDefinedList } from './icd10DefinedList'

export interface IndicationFieldChange {
  field: string
  label: string
  before: string | null
  after: string | null
  /** Long reference text: before and after are null, the row reads "Text changed". */
  kind?: 'text'
}

export interface IndicationDiffEntry {
  indicationId: string
  name: string
  kind: 'added' | 'removed' | 'modified'
  /** Defined-list ICD-10 codes, formatted and sorted; a range is one code. */
  codes: { added: string[]; removed: string[] }
  /** Engine (DDE-driving) fields, display-formatted, then reference text fields on a modified indication. */
  fields: IndicationFieldChange[]
}

export interface IndicationSetDiff {
  entries: IndicationDiffEntry[]
  unchangedCount: number
}

/** Structural subset of `Indication`, so both worker and admin indications fit. */
export type IndicationDiffInput = Pick<Indication, 'id' | 'name'> &
  Partial<Pick<Indication, ReferenceField>> &
  DdeIndicationInput

/** The indication fields `buildDDERowsFromDrugIndications` reads, besides name and ICD-10 codes. */
export const INDICATION_FIELD_LABELS = {
  type: 'On or off label',
  ageMin: 'Minimum age',
  ageMinUom: 'Minimum age unit',
  ageMax: 'Maximum age',
  ageMaxUom: 'Maximum age unit',
  frequencyMaxAdminDays: 'Max administration days',
  frequencyMaxFrequency: 'Max administrations',
  frequencyTimePeriod: 'Frequency period',
  maxDailyHcpcsUnits: 'Max units per day',
  maxUnitsOverTimeMaxHCPCSUnits: 'Max units over time',
  maxUnitsOverHcpcsMaxFrequency: 'Max units period',
  maxUnitsOverHcpcsTimePeriod: 'Max units period unit',
  hcpcsCodes: 'HCPCS codes',
} as const satisfies Partial<Record<keyof DdeIndicationInput, string>>

type EngineField = keyof typeof INDICATION_FIELD_LABELS

/** Reference-only fields: listed as changes, but they never change a DDE row. */
export const INDICATION_REFERENCE_FIELD_LABELS = {
  name: 'Name',
  description: 'Description',
  dosingSummary: 'Dosing summary',
  patientAttribute: 'Patient attribute',
  patientAttributeValue: 'Patient attribute value',
} as const

type ReferenceField = Exclude<
  keyof typeof INDICATION_REFERENCE_FIELD_LABELS,
  'name'
>

const LONG_TEXT = new Set<string>(['description', 'dosingSummary'])

const text = (value: unknown): string | null =>
  value === undefined || value === null || value === '' ? null : String(value)

const DISPLAY: Record<
  EngineField,
  (indication: DdeIndicationInput) => string | null
> = {
  type: ({ type }) => (type === 'ON_LABEL' ? 'On-label' : 'Off-label'),
  ageMin: ({ ageMin }) => text(parseAgeValue(ageMin)),
  ageMinUom: ({ ageMinUom }) => text(ageMinUom),
  ageMax: ({ ageMax }) => text(parseAgeValue(ageMax)),
  ageMaxUom: ({ ageMaxUom }) => text(ageMaxUom),
  frequencyMaxAdminDays: ({ frequencyMaxAdminDays }) =>
    text(toPositiveNumber(frequencyMaxAdminDays)),
  frequencyMaxFrequency: ({ frequencyMaxFrequency }) =>
    text(toPositiveNumber(frequencyMaxFrequency)),
  frequencyTimePeriod: ({ frequencyTimePeriod }) => text(frequencyTimePeriod),
  maxDailyHcpcsUnits: ({ maxDailyHcpcsUnits }) =>
    text(toPositiveNumber(maxDailyHcpcsUnits)),
  maxUnitsOverTimeMaxHCPCSUnits: ({ maxUnitsOverTimeMaxHCPCSUnits }) =>
    text(toPositiveNumber(maxUnitsOverTimeMaxHCPCSUnits)),
  maxUnitsOverHcpcsMaxFrequency: ({ maxUnitsOverHcpcsMaxFrequency }) =>
    text(toPositiveNumber(maxUnitsOverHcpcsMaxFrequency)),
  maxUnitsOverHcpcsTimePeriod: ({ maxUnitsOverHcpcsTimePeriod }) =>
    text(maxUnitsOverHcpcsTimePeriod),
  hcpcsCodes: ({ hcpcsCodes }) =>
    text([...new Set(hcpcsCodes ?? [])].sort().join(', ')),
}

const definedCodes = (indication?: DdeIndicationInput): Set<string> =>
  new Set(
    (indication?.icd10Codes ?? [])
      .filter((code) => code?.code?.trim() && isIcd10InDefinedList(code))
      .map(({ code }) => RefData.formatIcd10Code(code)),
  )

const missingFrom = (from: Set<string>, other: Set<string>) =>
  [...from].filter((code) => !other.has(code)).sort(RefData.compareIcd10Codes)

const referenceFields = (
  before: IndicationDiffInput,
  after: IndicationDiffInput,
): IndicationFieldChange[] =>
  (
    Object.keys(INDICATION_REFERENCE_FIELD_LABELS) as (
      | ReferenceField
      | 'name'
    )[]
  ).flatMap((field) => {
    const [previous, next] = [text(before[field]), text(after[field])]
    if (previous === next) return []
    const label = INDICATION_REFERENCE_FIELD_LABELS[field]
    return LONG_TEXT.has(field)
      ? [{ field, label, before: null, after: null, kind: 'text' as const }]
      : [{ field, label, before: previous, after: next }]
  })

const diffEntry = (
  before: IndicationDiffInput | undefined,
  after: IndicationDiffInput | undefined,
): IndicationDiffEntry => {
  const subject = (after ?? before)!
  const [beforeCodes, afterCodes] = [definedCodes(before), definedCodes(after)]
  const fields = (
    Object.keys(INDICATION_FIELD_LABELS) as EngineField[]
  ).flatMap((field) => {
    const previous = before ? DISPLAY[field](before) : null
    const next = after ? DISPLAY[field](after) : null
    return previous === next
      ? []
      : [
          {
            field,
            label: INDICATION_FIELD_LABELS[field],
            before: previous,
            after: next,
          },
        ]
  })
  return {
    indicationId: subject.id,
    name: subject.name,
    kind: !before ? 'added' : !after ? 'removed' : 'modified',
    codes: {
      added: missingFrom(afterCodes, beforeCodes),
      removed: missingFrom(beforeCodes, afterCodes),
    },
    fields:
      before && after ? [...fields, ...referenceFields(before, after)] : fields,
  }
}

const isChanged = ({ kind, codes, fields }: IndicationDiffEntry) =>
  kind !== 'modified' ||
  codes.added.length > 0 ||
  codes.removed.length > 0 ||
  fields.length > 0

/**
 * What changed between two indication sets, by indication id, in the terms
 * the engine sees: defined-list diagnosis codes and the DDE-driving fields,
 * plus the reference text on an indication present on both sides. Workflow
 * metadata (status, timestamps, confidence) is not compared.
 */
export const diffIndicationSets = (
  before: IndicationDiffInput[],
  after: IndicationDiffInput[],
): IndicationSetDiff => {
  const previous = new Map(
    before.map((indication) => [indication.id, indication]),
  )
  const next = new Map(after.map((indication) => [indication.id, indication]))
  const entries = [...new Set([...previous.keys(), ...next.keys()])].map((id) =>
    diffEntry(previous.get(id), next.get(id)),
  )
  const changed = entries.filter(isChanged)

  return {
    entries: changed.sort((left, right) => left.name.localeCompare(right.name)),
    unchangedCount: entries.length - changed.length,
  }
}
